import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminProductDistributionPlatformListing,
  DistributionPlatform,
  DocumentsApi,
  PatchedAdminDistributorProductDistributionInformationRequest,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import DistributionInformationModal, { DistributionInformationModalFields } from '../distribution-information-modal';
import { ExtendedDistributionInformation } from '../list-distribution-informations';
import { Document } from 'src/ui/types/document';

interface EditDistributionInformationProps {
  onDistributionInformationAdded: () => void;
  onClose: () => void;
  listings: AdminProductDistributionPlatformListing[];
  distributionPlatformsWithEditPermission: DistributionPlatform[];
  data: ExtendedDistributionInformation;
}

const EditDistributionInformation: FunctionComponent<EditDistributionInformationProps> = ({
  onDistributionInformationAdded,
  onClose,
  listings,
  distributionPlatformsWithEditPermission,
  data,
}) => {
  const [platformsWithNoDistributionInformation, setPlatformsWithNoDistributionInformation] = useState<
    AdminProductDistributionPlatformListing[]
  >([]);

  const { makeAuthenticatedApi, withApi, loading: apiLoading, error } = useApiCall();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback(
    (
      fieldsValues: StateValues<DistributionInformationModalFields>,
      productStartDate?: Date | null,
      productEndDate?: Date | null,
      uploadedImage?: Document | null,
    ) => {
      withApi(async () => {
        const params: PatchedAdminDistributorProductDistributionInformationRequest = {
          complianceProductName: fieldsValues.complianceProductName,
          useUnits: fieldsValues.useUnits === 'true',
          selfDisclosureRequired: fieldsValues.selfDisclosureRequired,
          investmentExperienceMinScore: Number(fieldsValues.investmentExperienceMinScore),
          serviceExperienceMinScore: Number(fieldsValues.serviceExperienceMinScore),
          costsvariables: {
            capitalType: fieldsValues.capitalType,
            costsBrokerageNominalAmountPercent: fieldsValues.costsBrokerageNominalAmountPercent,
            grantsNominalAmountPercent: fieldsValues.grantsNominalAmountPercent,
            costsBrokerageNavPercent: fieldsValues.costsBrokerageNavPercent,
            grantsNavPercent: fieldsValues.grantsNavPercent,
            costsTransactionDepotNominalAmountPercent: fieldsValues.costsTransactionDepotNominalAmountPercent,
            costsTransactionDepotNavPercent: fieldsValues.costsTransactionDepotNavPercent,
          },
        };

        if (fieldsValues.unitSteps) {
          params.unitSteps = fieldsValues.unitSteps;
        }
        if (fieldsValues.runtimeDate === 'false') {
          params.costsvariables!.runtimeYears = Number(fieldsValues.runtimeYears);
        } else {
          params.costsvariables!.runtimeYears = 0;
          params.costsvariables!.productStartDate = productStartDate;
          params.costsvariables!.productEndDate = productEndDate;
        }

        if (fieldsValues.productImage?.file) {
          const docResponse = await documentsApi.adminDocumentsCreate({
            file: fieldsValues.productImage.file,
          });
          params.productImageId = docResponse.id;
        } else if (uploadedImage) {
          params.productImageId = data.productImage.id;
        } else {
          params.productImageId = null;
        }

        await api.adminListingsDistributionInformationPartialUpdate({
          id: data.productDistributionPlatformListingId,
          patchedAdminDistributorProductDistributionInformationRequest: params,
        });
        onDistributionInformationAdded();
      });
    },
    [api, data.id],
  );

  useEffect(() => {
    setPlatformsWithNoDistributionInformation(
      listings.filter((listing) =>
        distributionPlatformsWithEditPermission.some((platform) => platform.id === listing.distributionPlatformId),
      ),
    );
  }, [JSON.stringify(listings), JSON.stringify(distributionPlatformsWithEditPermission)]);

  return (
    <DistributionInformationModal
      platformsWithNoDistributionInformation={platformsWithNoDistributionInformation}
      onSubmit={onSubmit}
      onClose={onClose}
      modalHeader={<Translate name="distributionInformation.edit" />}
      loading={apiLoading}
      error={error}
      data={data}
    ></DistributionInformationModal>
  );
};

export default EditDistributionInformation;
